import { useSetRecoilState } from 'recoil';
import {
  ApiPaginatedResponseBodyOfPromotionResponse,
  PromotionBody,
  PromotionResponse,
  PromotionsApi,
  PromotionTypeResponse
} from '../../../../API';
import {
  CacheManager,
  DataManagerHelpers,
  DataManagerHook,
  DataManagerHookReturnType,
  IDataManagerHookProps
} from '../../../DataManager';

export interface PromotionSearchFilters {
  text?: string;
  status?: string;
  audience?: string;
  promoType?: string;
}

export interface PromotionsHookReturnType extends DataManagerHookReturnType<PromotionResponse, PromotionBody> {
  startPromotion: (promotion: PromotionResponse) => Promise<boolean>;
  endPromotion: (promotion: PromotionResponse) => Promise<boolean>;
  getPromotionTypes: () => Promise<PromotionTypeResponse[]>;
  searchPromotions: (params: {
    page: number;
    limit: number;
    filters?: PromotionSearchFilters;
  }) => Promise<ApiPaginatedResponseBodyOfPromotionResponse>;
}

export function PromotionsHook(
  params: IDataManagerHookProps<PromotionResponse, PromotionBody>
): PromotionsHookReturnType {
  const dataManagerHook = DataManagerHook<PromotionResponse, PromotionBody>(params);
  const api = params.useApiHook() as PromotionsApi;
  const setCache = useSetRecoilState(params.state.withDataCache);
  const { addRecordToCache, addRecordsToCache } = CacheManager(params.idField, setCache);
  const { handleApiError } = DataManagerHelpers({ name: params.name, toString });

  /**
   * Fetches a single page of promotions. Filtering is applied by the API, so the
   * caller never needs the full promotion list.
   */
  const searchPromotions = async (searchParams: { page: number; limit: number; filters?: PromotionSearchFilters }) => {
    try {
      const response = await api.getPaginated(
        searchParams.limit,
        searchParams.page,
        undefined,
        searchParams.filters?.text,
        searchParams.filters?.status,
        searchParams.filters?.audience,
        searchParams.filters?.promoType
      );
      const body = response.data.body;
      if (body.results?.length) {
        addRecordsToCache(body.results);
      }
      return body;
    } catch (err) {
      handleApiError(err, 'search_promotions');
      return { results: [], meta: {} } as unknown as ApiPaginatedResponseBodyOfPromotionResponse;
    }
  };

  const startPromotion = async (promotion: PromotionResponse) => {
    try {
      const response = await api.startPromotion(promotion.id);
      addRecordToCache(response.data.body);
      return true;
    } catch (err) {
      handleApiError(err, 'start_promotion');
      return false;
    }
  };

  const endPromotion = async (promotion: PromotionResponse) => {
    try {
      const response = await api.endPromotion(promotion.id);
      addRecordToCache(response.data.body);
      return true;
    } catch (err) {
      handleApiError(err, 'end_promotion');
      return false;
    }
  };

  const getPromotionTypes = async () => {
    try {
      const response = await api.getPromotionTypes();
      return response.data.body;
    } catch (err) {
      handleApiError(err, 'get_promotion_types');
      return [];
    }
  };

  return {
    ...dataManagerHook,
    startPromotion,
    endPromotion,
    getPromotionTypes,
    searchPromotions
  };
}
